// FinancialPromotionResponsesDashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { subscribeToFinancialPromotionResponses } from "../services/financialPromotionsRealtime";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import StatusButton from "../../../DesignSystem/Buttons/StatusButton";

import { APP_ROUTES, buildRoute } from "../../../../constants/appRoutes";

import "../../../../css/Marketing/FinancialPromotionContentGenerator/FinancialPromotionsContentDashboard.css";
import { ReactComponent as WebsiteIcon } from "../../../../assets/icons/financialPromotions/website-1.svg";
import { ReactComponent as LinkedInIcon } from "../../../../assets/icons/financialPromotions/linkedIn.svg";
import { ReactComponent as FacebookIcon } from "../../../../assets/icons/financialPromotions/facebook.svg";
import { ReactComponent as InstagramIcon } from "../../../../assets/icons/financialPromotions/instagram.svg";
import { ReactComponent as TikTokIcon } from "../../../../assets/icons/financialPromotions/tiktok.svg";
import { ReactComponent as ArrowRightIcon } from "../../../../assets/icons/financialPromotions/ArrowRight.svg";
import {
  normaliseExternalUrl,
  subscribeFinancialPromotionAdviserProfiles,
} from "../../../../context/Adviser/Utils/FinancialPromotionAdviserHelpers";
import OutlinedStatusButton from "../../../DesignSystem/Buttons/OutlinedStatusButtons";

const breadcrumbItems = [
  { label: "Staff Portal", to: APP_ROUTES.DEPARTMENT_ALL_STAFF },
  { label: "Marketing", to: APP_ROUTES.DEPARTMENT_MARKETING },
  { label: "Social Media Content Management" },
];

const toDateObject = (dateValue) => {
  if (!dateValue) return null;

  if (dateValue?.toDate) return dateValue.toDate();

  if (typeof dateValue === "object" && typeof dateValue.seconds === "number") {
    return new Date(dateValue.seconds * 1000);
  }

  const parsedDate = new Date(dateValue);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getTimeValue = (dateValue) => {
  const parsedDate = toDateObject(dateValue);
  return parsedDate ? parsedDate.getTime() : 0;
};

const getLatestResponseTime = (response = {}) => {
  return getTimeValue(response.updatedAt || response.createdAt);
};

const formatDateTime = (dateValue) => {
  const parsedDate = toDateObject(dateValue);

  if (!parsedDate) return "Not available";

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateOnly = (dateValue) => {
  const parsedDate = toDateObject(dateValue);

  if (!parsedDate) return "Not available";

  return parsedDate.toLocaleDateString("en-GB");
};

const getFirstValue = (...values) => {
  return values.find((value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const getNormalisedResponse = (response = {}) => {
  const content = response.content || {};

  return {
    ...response,

    id: response.id || response.responseId || "",
    responseId: response.responseId || response.id || "",
    promotionId: response.promotionId || "",

    adviserEmail: getFirstValue(response.adviserEmail, "Unknown adviser"),
    adviserName: getFirstValue(
      response.adviserName,
      response.name,
      response.adviser,
      response.createdByName,
      "",
    ),
    tradingName: getFirstValue(response.tradingName, response.companyName, ""),

    contentType: getFirstValue(response.contentType, ""),
    contentTypes: toArray(response.contentTypes),

    platform: getFirstValue(response.platform, ""),
    platforms: toArray(response.platforms),

    audience: getFirstValue(response.audience, ""),
    objective: getFirstValue(response.objective, ""),
    tone: getFirstValue(response.tone, ""),
    notes: getFirstValue(response.notes, response.extraNotes, ""),

    caption: getFirstValue(response.caption, content.caption, ""),
    shortCaption: getFirstValue(
      response.shortCaption,
      content.shortCaption,
      "",
    ),
    graphicText: getFirstValue(response.graphicText, content.graphicText, ""),
    suggestedImageBrief: getFirstValue(
      response.suggestedImageBrief,
      content.suggestedImageBrief,
      "",
    ),
    regulatoryTextUsed: getFirstValue(
      response.regulatoryTextUsed,
      content.regulatoryTextUsed,
      "",
    ),

    hashtags: toArray(getFirstValue(response.hashtags, content.hashtags, [])),
    complianceNotes: toArray(
      getFirstValue(response.complianceNotes, content.complianceNotes, []),
    ),
    warnings: toArray(getFirstValue(response.warnings, content.warnings, [])),

    status: response.status || "Created",
    hootsuiteStatus: response.hootsuiteStatus || "Not Exported",

    createdBy: response.createdBy || "",
    createdAt: response.createdAt || "",
    updatedBy: response.updatedBy || "",
    updatedAt: response.updatedAt || "",
  };
};

const getPromotionGroupId = (response = {}) => {
  // New records should always have promotionId.
  // The fallback keeps old/legacy records visible, but they cannot be grouped
  // unless they share the same promotionId.
  return (
    response.promotionId ||
    `legacy-${response.responseId || response.id || response.storagePath || ""}`
  );
};

const getResponseRouteId = (response = {}) => {
  return getPromotionGroupId(response);
};

const getNormalisedStatus = (status) => {
  return String(status || "Created").toLowerCase();
};

const getStatusLabel = (status) => {
  const normalisedStatus = getNormalisedStatus(status);

  if (normalisedStatus.includes("approved")) return "Approved";
  if (normalisedStatus.includes("rejected")) return "Rejected";
  if (normalisedStatus.includes("pending")) return "Pending";
  if (normalisedStatus.includes("created")) return "Needs Review";

  return status || "Needs Review";
};

const isApproved = (status) => {
  return getNormalisedStatus(status).includes("approved");
};

const getStatusColor = (status) => {
  const normalisedStatus = getNormalisedStatus(status);

  if (normalisedStatus.includes("approved")) return "green";
  if (normalisedStatus.includes("rejected")) return "red";
  if (normalisedStatus.includes("created")) return "crimson";
  if (normalisedStatus.includes("pending")) return "pending";

  return "blue";
};

const getAdviserEmail = (response = {}) => {
  return response.adviserEmail || "Unknown adviser";
};

const getAdviserName = (response = {}, fallback = "Unknown adviser") => {
  return (
    response.adviserName ||
    response.name ||
    response.adviser ||
    response.createdByName ||
    fallback
  );
};

const getTradingName = (response = {}, fallback = "Unknown company") => {
  return response.tradingName || response.companyName || fallback;
};

const getPostTitle = (response = {}) => {
  if (response.title) return response.title;
  if (response.objective) return response.objective;
  if (response.shortCaption) return response.shortCaption;

  const caption = response.caption || "";

  if (caption) {
    const firstSentence = caption.split(/[.!?]/)[0];

    return firstSentence.length > 70
      ? `${firstSentence.slice(0, 70)}...`
      : firstSentence;
  }

  return `${response.contentType || "Content"} post`;
};

const getPostTags = (response = {}) => {
  const tagValues = [
    response.contentTypes,
    response.contentType,
    response.platforms,
    response.platform,
  ]
    .flatMap((value) => (Array.isArray(value) ? value : [value]))
    .flatMap((value) => String(value || "").split(","))
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => `${value.charAt(0).toUpperCase()}${value.slice(1)}`);

  return tagValues.filter(
    (tag, index) =>
      tagValues.findIndex(
        (candidate) => candidate.toLowerCase() === tag.toLowerCase(),
      ) === index,
  );
};

const groupResponsesByPromotionId = (responses = []) => {
  const groups = responses.reduce((acc, response) => {
    const promotionGroupId = getPromotionGroupId(response);

    if (!promotionGroupId) return acc;

    if (!acc[promotionGroupId]) {
      acc[promotionGroupId] = [];
    }

    acc[promotionGroupId].push(response);

    return acc;
  }, {});

  return Object.entries(groups)
    .map(([promotionGroupId, groupedResponses]) => {
      const versions = [...groupedResponses].sort(
        (a, b) => getLatestResponseTime(b) - getLatestResponseTime(a),
      );

      const approvedVersion = versions.find((version) =>
        isApproved(version.status),
      );

      return {
        ...versions[0],
        promotionGroupId,
        versions,
        versionCount: versions.length,
        status: approvedVersion ? "Approved" : versions[0].status || "Created",
        approvedAt:
          approvedVersion?.approvedAt ||
          approvedVersion?.updatedAt ||
          approvedVersion?.createdAt ||
          "",
      };
    })
    .sort((a, b) => getLatestResponseTime(b) - getLatestResponseTime(a));
};

const applyApprovedNavigationState = (
  responses = [],
  approvedResponseId = "",
) => {
  if (!approvedResponseId) return responses;

  return responses.map((response) => {
    const matchesResponse =
      response.responseId === approvedResponseId ||
      response.id === approvedResponseId;

    if (!matchesResponse) return response;

    const approvedDate = new Date();

    return {
      ...response,
      status: "Approved",
      approvedAt: response.approvedAt || approvedDate,
      updatedAt: approvedDate,
    };
  });
};

const AdviserSocialIcon = ({ label, value, Icon }) => {
  const url = normaliseExternalUrl(value);

  if (!url) return null;

  return (
    <button
      type="button"
      className="fp-adviser-social-icon"
      onClick={(event) => {
        event.stopPropagation();
        window.open(url, "_blank", "noopener,noreferrer");
      }}
      title={`Open ${label}`}
      aria-label={`Open ${label}`}
    >
      <Icon />
    </button>
  );
};

const AdviserSocialIcons = ({ adviser = {} }) => {
  const links = [
    { label: "Website", value: adviser.website, Icon: WebsiteIcon },
    { label: "LinkedIn", value: adviser.linkedin, Icon: LinkedInIcon },
    { label: "Facebook", value: adviser.facebook, Icon: FacebookIcon },
    { label: "Instagram", value: adviser.instagram, Icon: InstagramIcon },
    { label: "TikTok", value: adviser.tiktok, Icon: TikTokIcon },
  ];

  const hasLinks = links.some((link) => String(link.value || "").trim());

  if (!hasLinks) return null;

  return (
    <div className="fp-adviser-social-icons">
      {links.map((link) => (
        <AdviserSocialIcon
          key={link.label}
          label={link.label}
          value={link.value}
          Icon={link.Icon}
        />
      ))}
    </div>
  );
};

const FinancialPromotionResponsesDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [adviserProfiles, setAdviserProfiles] = useState({});
  const [responses, setResponses] = useState([]);
  const [selectedAdviserEmail, setSelectedAdviserEmail] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");

  // const loadResponses = async () => {
  //   try {
  //     setLoading(true);
  //     setError("");

  //     const data = await getFinancialPromotionResponses();

  //     const normalised = Array.isArray(data)
  //       ? data.map(getNormalisedResponse)
  //       : [];

  //     setResponses(normalised);
  //   } catch (err) {
  //     setError(err.message || "Failed to load previous responses.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // useEffect(() => {
  //   loadResponses();
  // }, []);

  useEffect(() => {
    setLoading(true);
    setError("");

    const unsubscribe = subscribeToFinancialPromotionResponses({
      onData: (data) => {
        const normalised = Array.isArray(data)
          ? data.map(getNormalisedResponse)
          : [];

        const approvedResponseId = location.state?.approvedResponseId || "";

        setResponses(
          applyApprovedNavigationState(normalised, approvedResponseId),
        );
        setLoading(false);
      },
      onError: (err) => {
        setError(err.message || "Failed to load previous responses.");
        setLoading(false);
      },
    });

    return () => {
      unsubscribe?.();
    };
  }, [location.state]);

  const adviserEmails = useMemo(() => {
    return [
      ...new Set(
        responses
          .map((response) => response.adviserEmail)
          .filter((email) => email && email !== "Unknown adviser"),
      ),
    ];
  }, [responses]);

  useEffect(() => {
    if (!adviserEmails.length) {
      setAdviserProfiles({});
      return undefined;
    }

    const unsubscribe = subscribeFinancialPromotionAdviserProfiles({
      adviserEmails,
      onData: setAdviserProfiles,
      onError: (err) => {
        console.error("Failed to load adviser profile details:", err);
      },
    });

    return () => {
      unsubscribe?.();
    };
  }, [adviserEmails]);

  const sortedResponses = useMemo(() => {
    return [...responses].sort((a, b) => {
      const emailCompare = getAdviserEmail(a)
        .toLowerCase()
        .localeCompare(getAdviserEmail(b).toLowerCase());

      if (emailCompare !== 0) return emailCompare;

      return getLatestResponseTime(b) - getLatestResponseTime(a);
    });
  }, [responses]);

  const filteredResponses = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) return sortedResponses;

    return sortedResponses.filter((response) => {
      const searchableText = [
        response.promotionId,
        response.responseId,
        response.adviserEmail,
        response.adviserName,
        response.tradingName,
        response.companyName,
        response.contentType,
        response.platform,
        response.status,
        response.caption,
        response.shortCaption,
        response.graphicText,
        response.objective,
        response.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [searchTerm, sortedResponses]);

  const adviserGroups = useMemo(() => {
    const groups = filteredResponses.reduce((acc, response) => {
      const email = getAdviserEmail(response);

      const liveProfile = adviserProfiles[email] || {};

      if (!acc[email]) {
        acc[email] = {
          email,
          adviserName:
            liveProfile.adviserName || getAdviserName(response, email),
          tradingName:
            liveProfile.tradingName || getTradingName(response, email),
          website: liveProfile.website || response.website || "",
          linkedin:
            liveProfile.linkedin ||
            response.linkedin ||
            response.linkedIn ||
            "",
          facebook: liveProfile.facebook || response.facebook || "",
          instagram: liveProfile.instagram || response.instagram || "",
          tiktok:
            liveProfile.tiktok || response.tiktok || response.tikTok || "",
          rawResponses: [],
        };
      }
      acc[email].rawResponses.push(response);

      if (!acc[email].adviserName || acc[email].adviserName === email) {
        acc[email].adviserName =
          liveProfile.adviserName || getAdviserName(response, email);
      }

      if (!acc[email].tradingName || acc[email].tradingName === email) {
        acc[email].tradingName =
          liveProfile.tradingName || getTradingName(response, email);
      }

      acc[email].website = liveProfile.website || acc[email].website;
      acc[email].linkedin = liveProfile.linkedin || acc[email].linkedin;
      acc[email].facebook = liveProfile.facebook || acc[email].facebook;
      acc[email].instagram = liveProfile.instagram || acc[email].instagram;
      acc[email].tiktok = liveProfile.tiktok || acc[email].tiktok;
      return acc;
    }, {});

    return Object.values(groups)
      .map((group) => {
        const groupedPromotions = groupResponsesByPromotionId(
          group.rawResponses,
        );

        const latestPromotion = groupedPromotions[0];

        const latestApprovedPromotion =
          groupedPromotions
            .filter((response) => isApproved(response.status))
            .sort(
              (a, b) => getLatestResponseTime(b) - getLatestResponseTime(a),
            )[0] || null;

        const now = new Date();

        const thisMonthPromotions = groupedPromotions.filter((response) => {
          const responseDate = toDateObject(
            response.updatedAt || response.createdAt,
          );

          if (!responseDate) return false;

          return (
            responseDate.getMonth() === now.getMonth() &&
            responseDate.getFullYear() === now.getFullYear()
          );
        });

        const isSameMonth = (dateValue) => {
          const responseDate = toDateObject(dateValue);

          if (!responseDate) return false;

          return (
            responseDate.getMonth() === now.getMonth() &&
            responseDate.getFullYear() === now.getFullYear()
          );
        };

        const createdThisMonth = groupedPromotions.filter((response) =>
          isSameMonth(response.createdAt),
        ).length;

        const approvedThisMonth = groupedPromotions.filter((response) => {
          if (!isApproved(response.status)) return false;

          return isSameMonth(
            response.approvedAt || response.updatedAt || response.createdAt,
          );
        }).length;

        return {
          email: group.email,
          adviserName: group.adviserName,
          tradingName: group.tradingName,

          website: group.website,
          linkedin: group.linkedin,
          facebook: group.facebook,
          instagram: group.instagram,
          tiktok: group.tiktok,

          responses: groupedPromotions,

          totalGenerated: groupedPromotions.length,
          postsApproved: groupedPromotions.filter((response) =>
            isApproved(response.status),
          ).length,
          createdThisMonth,
          approvedThisMonth,
          totalThisMonth: createdThisMonth,
          lastCreated: latestPromotion?.updatedAt || latestPromotion?.createdAt,
          lastApproved:
            latestApprovedPromotion?.approvedAt ||
            latestApprovedPromotion?.updatedAt ||
            latestApprovedPromotion?.createdAt ||
            "",
        };
      })
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [filteredResponses, adviserProfiles]);

  useEffect(() => {
    if (!adviserGroups.length) {
      setSelectedAdviserEmail("");
      return;
    }

    const selectedStillExists = adviserGroups.some(
      (group) => group.email === selectedAdviserEmail,
    );

    if (!selectedAdviserEmail || !selectedStillExists) {
      setSelectedAdviserEmail(adviserGroups[0].email);
    }
  }, [adviserGroups, selectedAdviserEmail]);

  const selectedAdviser = useMemo(() => {
    return adviserGroups.find((group) => group.email === selectedAdviserEmail);
  }, [adviserGroups, selectedAdviserEmail]);

  const selectedPromotions = selectedAdviser?.responses || [];

  const openPromotionDetails = (promotion) => {
    navigate(
      buildRoute.socialMediaContentResponseDetail(
        getResponseRouteId(promotion),
      ),
      {
        state: {
          response: promotion,
          versions: promotion.versions || [promotion],
        },
      },
    );
  };

  const handleGenerateNewContent = () => {
    sessionStorage.removeItem("financialPromotionGeneratorPrefill");

    navigate(APP_ROUTES.SOCIAL_MEDIA_CONTENT_CREATION, {
      state: {
        resetGeneratorPrefill: true,
      },
    });
  };

  return (
    <div className="main-content">
      <Sidebar />

      <Helmet>
        <title>Social Media Content Management</title>
        <meta
          name="description"
          content="Review previously generated financial promotion and social media content by adviser."
        />
      </Helmet>

      <main className="Main-Container gap-xl column">
        <Breadcrumb items={breadcrumbItems} />

        <section className="fp-social-dashboard-header">
          <div>
            <h3 className="FP_H3 primary">
              Social Media Content Management
            </h3>
          </div>

          <div className="fp-social-dashboard-actions">
            <SearchField
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search adviser or content"
            />
            <SecondaryButton
              type="button"
              label="Generate New Content"
              onClick={handleGenerateNewContent}
            />
          </div>
        </section>

        {error && <div className="fp-error FP_Content">{error}</div>}

        <section className="fp-social-dashboard-grid">
          <aside className="fp-adviser-dashboard-list" aria-label="Advisers">
            {loading && (
              <p className="FP_Help_Text secondary">
                Loading previous responses...
              </p>
            )}

            {!loading && adviserGroups.length === 0 && (
              <div className="fp-empty-state">
                <p className="FP_Content primary">No responses found.</p>

                <span className="FP_Help_Text secondary">
                  Try changing your search or generate new content first.
                </span>
              </div>
            )}

            {!loading &&
              adviserGroups.map((group) => {
                const isActive = group.email === selectedAdviserEmail;

                return (
                  <button
                    key={group.email}
                    type="button"
                    className={`fp-adviser-dashboard-card ${
                      isActive ? "fp-adviser-dashboard-card--active" : ""
                    }`}
                    onClick={() => setSelectedAdviserEmail(group.email)}
                  >
                    <span className="fp-adviser-dashboard-main">
                      <span className="FP_Help_Text text-black">
                        {group.tradingName || group.email}
                      </span>

                      <strong className="FP_Content_Semibold text-black">
                        {group.adviserName || group.email}
                      </strong>

                      <span className="FP_Help_Text text-black">
                        Monthly Stats
                      </span>
                      <span className="fp-adviser-dashboard-stats">
                        <span className="FP_Help_Text">
                          <small className="text-gray">Created</small>
                          <small className="text-black">
                            {group.createdThisMonth}
                          </small>
                        </span>

                        <span className="FP_Help_Text">
                          <small className="text-gray">Approved</small>
                          <small className="text-black">
                            {group.approvedThisMonth}
                          </small>
                        </span>
                      </span>
                    </span>
                  </button>
                );
              })}
          </aside>

          <section className="fp-content-feed-panel">
            {selectedAdviser ? (
              <>
                <div>
                  <div className="fp-content-feed-header">
                    <div>
                      <p className="FP_Help_Text text-black">
                        {selectedAdviser.tradingName || selectedAdviser.email}
                      </p>

                      <h2 className="FP_Subtitle text-black">
                        {selectedAdviser.adviserName || selectedAdviser.email}
                      </h2>

                      <p className="FP_Help_Text text-gray">
                        {selectedAdviser.email}
                      </p>

                      <AdviserSocialIcons adviser={selectedAdviser} />
                    </div>
                    <div className="fp-selected-adviser-stat-group">
                      <span className="FP_Help_Text_Semibold text-black">
                        Total Stats
                      </span>

                      <div className="fp-selected-adviser-stat-row FP_Body">
                        <small className="text-gray">Created</small>
                        <strong className="text-black">
                          {selectedAdviser.totalGenerated}
                        </strong>
                      </div>

                      <div className="fp-selected-adviser-stat-row FP_Body">
                        <small className="text-gray">Approved</small>
                        <strong className="text-black">
                          {selectedAdviser.postsApproved}
                        </strong>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fp-content-feed-list">
                  {selectedPromotions.map((promotion) => {
                    const rowKey = promotion.promotionGroupId;

                    return (
                      <article
                        key={rowKey}
                        className="fp-content-feed-card"
                        style={{ cursor: "pointer" }}
                        onClick={() => openPromotionDetails(promotion)}
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openPromotionDetails(promotion);
                          }
                        }}
                        aria-label={`View details for promotion created on ${formatDateTime(
                          promotion.createdAt,
                        )} with status ${getStatusLabel(promotion.status)}`}
                      >
                        <div className="fp-content-feed-card__body">
                          <time
                            className="FP_Help_Text text-black"
                            dateTime={toDateObject(
                              promotion.updatedAt || promotion.createdAt,
                            )?.toISOString()}
                          >
                            {formatDateTime(
                              promotion.updatedAt || promotion.createdAt,
                            )}
                          </time>

                          <h3 className="FP_Content_Semibold text-black">
                            {getPostTitle(promotion)}
                          </h3>

                          <div className="fp-feed-type-row">
                            {/* <span className="FP_Help_Text text-gray">Type</span> */}

                            <span className="fp-feed-tags">
                              {getPostTags(promotion).map((tag) => (
                                <span key={tag} className="fp-feed-tag">
                                  {tag}
                                </span>
                              ))}

                              {promotion.versionCount > 1 && (
                                <span className="fp-feed-tag">
                                  {promotion.versionCount} responses
                                </span>
                              )}
                            </span>
                          </div>
                        </div>

                        <div className="fp-content-feed-card__actions">
                          <OutlinedStatusButton
                            label={getStatusLabel(promotion.status)}
                            color={getStatusColor(promotion.status)}
                          />

                          <button
                            type="button"
                            className="fp-feed-menu-button"
                            // onClick={() => openPromotionDetails(promotion)}
                            aria-label="Open promotion responses"
                          >
                            <ArrowRightIcon />
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="fp-empty-state">
                <p className="FP_Content primary">Select an adviser.</p>

                <span className="FP_Help_Text secondary">
                  Adviser content will appear here.
                </span>
              </div>
            )}
          </section>
        </section>
      </main>
    </div>
  );
};

export default FinancialPromotionResponsesDashboard;
